import { resetAfterLoginPath, selectAfterLoginPath, selectInstitution, } from '../../../store/features/system';
import { SystemActions } from '../../../store/features/system/types';
import { API } from '../../../vendor/api';
import { actions as coreActions, AngularServices, getAngularService, localeService, PlanTypes, ToastType, } from '../../core';
import * as types from '../constants/action-types';
import { AFTER_LOGIN_REDIRECT_TIMEOUT, } from '../constants/constants';
import { getUserCurrency } from '../helpers';
import { replaceSubdomain, setQueryParameter } from '../../../common/utils/url';
const SIGNUP_ACCOUNT_CREATED = 'Account Created at Sign Up';
const { LOGIN, LOGIN_WITH_PROVIDER } = types;
export const redirectAfterSignUp = (redirectState) => () => {
    const { routeName, routeParams } = redirectState ?? {
        routeName: 'account.dashboard',
        routeParams: { afterSignup: true },
    };
    setTimeout(() => {
        const $state = getAngularService('$state');
        $state.go(routeName, routeParams);
    }, AFTER_LOGIN_REDIRECT_TIMEOUT);
};
export const handleSignUpSuccess = (redirectState) => (dispatch) => {
    dispatch(redirectAfterSignUp(redirectState));
};
export const removeSignInError = () => (dispatch) => dispatch(coreActions.removeError(LOGIN.NAME));
const getLoginErrorToast = (error) => {
    const toast = { type: ToastType.error };
    if (!Boolean(error)) {
        toast.message = localeService.formatMessageById('server-offline.body');
    }
    else if (error.status === 401) {
        toast.message = error?.data?.error?.message;
    }
    else if (error.status === 429) {
        toast.message = localeService.formatMessageById('tooManyAttempts');
    }
    else if (!Boolean(error?.data?.error?.code) &&
        Boolean(error?.data?.error?.message)) {
        toast.message = error?.data?.error?.message;
    }
    return toast;
};
export const handleLoginError = (error) => (dispatch) => {
    const toast = getLoginErrorToast(error);
    dispatch(coreActions.addToast(toast.type, toast.message));
};
export const redirectAfterLogin = () => (dispatch, getState) => {
    setTimeout(() => {
        const cannyFactory = getAngularService(AngularServices.CannyFactory);
        const cannyRedirectUrl = cannyFactory.getRedirectUrl();
        const afterLoginPath = selectAfterLoginPath(getState());
        if (cannyRedirectUrl || !afterLoginPath) {
            const $state = getAngularService('$state');
            $state.go('account.dashboard', { afterLogin: true, cannyRedirectUrl });
        }
        else {
            history.pushState(null, null, afterLoginPath);
            dispatch(resetAfterLoginPath());
        }
    }, AFTER_LOGIN_REDIRECT_TIMEOUT);
};
export const handleLoginSuccess = () => (dispatch) => {
    dispatch(redirectAfterLogin());
};
export const login = (values) => async (dispatch, getState) => {
    const authHelpers = getAngularService('authHelpers');
    const trackingFactory = getAngularService('trackingFactory');
    const institution = selectInstitution(getState());
    const institutionId = institution?.id ?? null;
    dispatch({ type: LOGIN.REQUEST });
    const anonymousId = trackingFactory.segment.getAnonymousId();
    try {
        await authHelpers.login({ rememberMe: values.rememberMe }, { ...values, institutionId, anonymousId });
        dispatch({ type: LOGIN.SUCCESS });
        dispatch(handleLoginSuccess());
    }
    catch (error) {
        dispatch({
            type: LOGIN.ERROR,
            payload: { error, shouldStoreError: true },
        });
        dispatch(handleLoginError(error));
    }
};
export const storePendingUser = (values) => (dispatch) => {
    dispatch({ type: SystemActions.SET_USER, payload: values });
};
export const getSupportedCurrencies = async () => {
    return API.user.getSupportedCurrency();
};
export const createAccount = async (values, recaptchaToken) => {
    const locationService = getAngularService('UserLocationDetails');
    const browserService = getAngularService('Browser');
    const userService = getAngularService('User');
    const shapedValues = {
        ...values,
        confirmEmail: values.email,
    };
    const systemInfo = browserService.getSystemInfo();
    const locationResponse = await locationService.getLocationDetails();
    const locationDetails = locationResponse.toJSON();
    locationDetails.location.currency.code = await getUserCurrency(locationDetails);
    return userService.save({
        ...shapedValues,
        ...systemInfo,
        ...locationDetails,
        recaptchaToken,
    }).$promise;
};
export const createAccountAndLogin = (signupFormData, recaptchaToken, providedNewUser, redirectState, accountType) => async (dispatch) => {
    const authService = getAngularService('LoopBackAuth');
    const institutionHelper = getAngularService('institutionHelper');
    const trackingFactory = getAngularService('trackingFactory');
    const anonymousId = trackingFactory.segment.getAnonymousId();
    const institution = await institutionHelper.getCurrentInstitution();
    const institutionId = institution.id ?? null;
    dispatch({ type: LOGIN.REQUEST });
    try {
        const newUser = Boolean(providedNewUser)
            ? providedNewUser
            : await createAccount({
                ...signupFormData,
                institutionId,
                anonymousId,
            }, recaptchaToken);
        dispatch({ type: LOGIN.SUCCESS });
        dispatch(handleSignUpSuccess(redirectState));
        authService.setUser(newUser.accessToken.id, newUser.id, newUser);
        authService.save();
        trackingFactory.global.track(SIGNUP_ACCOUNT_CREATED, {
            // For consistency, this has been sent as "pro" not "professional"
            current_subscription: accountType === 'professional' ? 'pro' : accountType,
            user_id: newUser.id,
        });
    }
    catch (error) {
        dispatch({
            type: LOGIN.ERROR,
            payload: { error, shouldStoreError: true },
        });
        dispatch(handleLoginError(error));
    }
};
export const getUserDataByInviteToken = (token) => async () => {
    return API.user.getUserDataByInviteToken(token);
};
export const createSubscription = async (email, countryCode, planId, postalCode, idempotencyKey, seats, couponCode, totalPrice) => {
    return API.stripe.createSubscription({
        email,
        countryCode,
        planId,
        postalCode: postalCode ?? '',
        idempotencyKey,
        seats,
        coupon: couponCode,
        quotedPrice: totalPrice,
    });
};
// export const deleteCustomer = async (customerId) => {
//   return await API.stripe.deleteCustomer({ customerId });
// };
export const createUserAccount = async (signupFormData, subscription, customer, paymentMethod) => {
    return API.stripe.createUserAccount({
        signupFormData,
        subscription,
        customer,
        paymentMethod,
    });
};
export const loginWithProvider = (provider, params) => async (dispatch) => {
    const Auth = getAngularService('Auth');
    const trackingFactory = getAngularService('trackingFactory');
    const meta = { provider };
    const anonymousId = trackingFactory.segment.getAnonymousId();
    dispatch({ type: LOGIN_WITH_PROVIDER.REQUEST, meta });
    try {
        await Auth.loginWithProvider(provider, { ...params, anonymousId });
        dispatch({ type: LOGIN_WITH_PROVIDER.SUCCESS, meta });
        dispatch(handleLoginSuccess());
    }
    catch (error) {
        dispatch({ type: LOGIN_WITH_PROVIDER.ERROR, meta });
        dispatch(handleLoginError(error));
    }
};
export const checkEmailUnique = (email, token) => {
    const userService = getAngularService('User');
    return userService.checkUnique({ email, token }).$promise;
};
export const checkRoomNameUnique = (roomName, institutionId = null, tokenToIgnore = null) => {
    const roomService = getAngularService('Room');
    return roomService.isUnique({
        slug: roomName,
        institutionId,
        tokenToIgnore,
    }).$promise;
};
/**
 * Creates a clinic account and logs the user in.
 */
export const createClinicAccountAndLogin = (accessToken, newUser, newInstitution) => async (dispatch) => {
    const loopbackAuth = getAngularService('LoopBackAuth');
    const auth = getAngularService('Auth');
    const windowService = getAngularService('$window');
    try {
        loopbackAuth.setUser(accessToken.id, newUser.id, newUser);
        loopbackAuth.save();
        await auth.rememberInstitutionAuthState(newInstitution.id);
        // Track that we created a new clinic account at signup
        const trackingFactory = getAngularService('trackingFactory');
        trackingFactory.global.track(SIGNUP_ACCOUNT_CREATED, {
            current_subscription: PlanTypes.Clinic,
            user_id: newUser.id,
        });
        dispatch({ type: LOGIN.SUCCESS });
        dispatch(handleSignUpSuccess());
        let redirectUrl = replaceSubdomain(windowService.location.origin, newInstitution.domain);
        redirectUrl = setQueryParameter(redirectUrl, 'modal', 'self-signup');
        windowService.location.href = redirectUrl;
    }
    catch (error) {
        dispatch({
            type: LOGIN.ERROR,
            payload: { error, shouldStoreError: true },
        });
        dispatch(handleLoginError(error));
    }
};
